import { useEffect } from "react";

/**
 * 🔎 useDocumentMeta — lightweight per-page meta manager (no react-helmet).
 *
 * Updates <title>, <meta name="description">, <meta name="keywords">,
 * <link rel="canonical">, and the og:/twitter: pair at runtime. Googlebot
 * renders our JS bundle and reads the updated tags. On unmount, we restore
 * the previous values so navigating back to "/" surfaces the home meta
 * instead of leaking a sub-page's title.
 *
 * Pass-through args:
 *   title       (string)     — full <title> string for the page
 *   description (string)     — meta description (≤ 160 chars recommended)
 *   keywords    (string?)    — comma-separated keyword list (optional)
 *   canonical   (string?)    — absolute canonical URL (defaults to current path)
 */
const KEY_TO_SELECTOR = {
  description: 'meta[name="description"]',
  keywords:    'meta[name="keywords"]',
  ogTitle:     'meta[property="og:title"]',
  ogDesc:      'meta[property="og:description"]',
  ogUrl:       'meta[property="og:url"]',
  twTitle:     'meta[name="twitter:title"]',
  twDesc:      'meta[name="twitter:description"]',
  canonical:   'link[rel="canonical"]',
};

const upsert = (selector, attr, value) => {
  if (!value) return null;
  let el = document.head.querySelector(selector);
  let created = false;
  if (!el) {
    el = document.createElement(selector.startsWith("link") ? "link" : "meta");
    if (selector.startsWith("link")) el.setAttribute("rel", "canonical");
    else if (selector.includes("property=")) el.setAttribute("property", selector.match(/property="([^"]+)"/)[1]);
    else el.setAttribute("name", selector.match(/name="([^"]+)"/)[1]);
    document.head.appendChild(el);
    created = true;
  }
  const prev = el.getAttribute(attr);
  el.setAttribute(attr, value);
  return { el, attr, prev, created };
};

export default function useDocumentMeta({ title, description, keywords, canonical } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const finalCanonical = canonical || `https://theexchangealliance.com${window.location.pathname}`;
    const tracked = [
      upsert(KEY_TO_SELECTOR.description, "content", description),
      upsert(KEY_TO_SELECTOR.keywords,    "content", keywords),
      upsert(KEY_TO_SELECTOR.ogTitle,     "content", title),
      upsert(KEY_TO_SELECTOR.ogDesc,      "content", description),
      upsert(KEY_TO_SELECTOR.ogUrl,       "content", finalCanonical),
      upsert(KEY_TO_SELECTOR.twTitle,     "content", title),
      upsert(KEY_TO_SELECTOR.twDesc,      "content", description),
      upsert(KEY_TO_SELECTOR.canonical,   "href",    finalCanonical),
    ].filter(Boolean);

    return () => {
      document.title = prevTitle;
      tracked.forEach((t) => {
        if (!t) return;
        if (t.created) t.el.remove();
        else if (t.prev !== null) t.el.setAttribute(t.attr, t.prev);
      });
    };
  }, [title, description, keywords, canonical]);
}
